import {PIXI} from '@colonist/pixi'
import {GameColor} from '../../GameColor'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {uiGameManager} from '../../../../shared/scripts/EntryShared'
import {TextTruncatedView} from '../../ui_canvas_engine/TextTruncatedView'
import {PlayerValidator} from '@colonist/game-mechanic-validators'
import {GameFont} from '../../GameFont'
import {TranslationObject} from '@colonist/i18n'
import {UIGameTradeOfferWaitIconView} from '../../trade/ui_trade_offers/UIGameTradeOfferWaitIconView'
import {PlayerColor} from '@colonist/game-states'
import {ToolTipText} from 'code/client/web/scripts/ui_game/ui_game_tooltip/UIWGameToolTipController'

export enum UIGamePlayerAvatarStatus {
    None,
    Accepted,
    Rejected,
    Pending,
    Embargoed,
}

export enum PlayerAvatarUsernameLocation {
    Bottom,
    Right,
}

export class UIGamePlayerAvatarView extends PIXI.Container {
    private static readonly cachedAvatars: Map<string, PIXI.RenderTexture | PIXI.Texture> = new Map()

    protected bgIcon: SpriteView
    protected playerIcon: SpriteView
    private statusIcon: SpriteView | undefined
    private readonly size: number
    private readonly iconSize: number
    private readonly playerNameH: number
    playerState: PlayerValidator
    private _status: UIGamePlayerAvatarStatus

    protected playerName: TextTruncatedView | undefined
    protected readonly playerNameLocation: PlayerAvatarUsernameLocation
    private readonly playerNameFontSize: GameFont.Size
    private readonly playerNameColor: string

    private avatarEmbargoOverlay?: PIXI.Sprite | undefined
    private waitIcon: UIGameTradeOfferWaitIconView | undefined

    constructor(x: number, y: number, size: number, container: PIXI.Container, playerState: PlayerValidator, showPlayerName: boolean = false, playerNameLocation: PlayerAvatarUsernameLocation = PlayerAvatarUsernameLocation.Bottom,
                playerNameFontSize: GameFont.Size = GameFont.Size.Small, playerNameColor: GameColor.Color = GameColor.Color.Gray0) {
        super()
        this.x = x
        this.y = y
        this.playerState = playerState
        this.size = size
        this.iconSize = showPlayerName ? this.size * 0.7 : this.size
        this.playerNameH = showPlayerName ? this.size * 0.3 : 0
        this._status = UIGamePlayerAvatarStatus.None

        this.playerNameLocation = playerNameLocation
        this.playerNameFontSize = playerNameFontSize
        this.playerNameColor = GameColor.getColor(playerNameColor)

        this.drawBg()
        void this.drawPlayerIcon()
        if(showPlayerName) this.drawPlayerName()

        container.addChild(this)
    }

    private async drawPlayerIcon() {
        const iconSizeRatioAccordingToBg = 0.6
        const iconMarginRatioAccordingToBg = 0.2
        const iconSize = this.bgIcon.width * iconSizeRatioAccordingToBg
        const iconMargin = this.bgIcon.width * iconMarginRatioAccordingToBg
        const dummyIcon = PIXI.Texture.EMPTY
        this.playerIcon = new SpriteView(iconMargin, iconMargin, iconSize, iconSize, this, dummyIcon)
        const actualIcon = await uiGameManager.playerAvatarController.getAvatarTexture(this.playerState)
        this.updatePlayerIcon(actualIcon)
    }

    private drawPlayerName() {
        if(this.playerNameLocation == PlayerAvatarUsernameLocation.Right) this.drawPlayerNameRight()
        else this.drawPlayerNameBottom()
    }

    private drawPlayerNameBottom() {
        const playerNameY = this.bgIcon.y + this.bgIcon.height
        const playerNameX = this.bgIcon.x - (this.size - this.bgIcon.width) / 2
        const playerNameText: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.playerState.username}}
        this.playerName = new TextTruncatedView(playerNameText, playerNameX, playerNameY, this.size, this.playerNameH, this, this.playerNameFontSize, this.playerNameColor)
        this.playerName.centerX()
        this.playerName.repositionY(false)
    }

    private drawPlayerNameRight() {
        const playerNameY = this.bgIcon.y + this.playerIcon.height / 2
        const playerNameX = this.bgIcon.x + this.bgIcon.width * 1.2
        const playerNameText: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.playerState.username}}
        this.playerName = new TextTruncatedView(playerNameText, playerNameX, playerNameY, this.size * 2, this.playerNameH, this, this.playerNameFontSize, this.playerNameColor)
    }

    updatePlayerIcon(icon: PIXI.Texture) {
        if(this.destroyed) return

        this.playerIcon.texture = icon
    }

    async refreshPlayerIcon() {
        const playerIcon = await uiGameManager.playerAvatarController.getAvatarTexture(this.playerState)
        this.updatePlayerIcon(playerIcon)
    }

    async updatePlayerColor(playerState: PlayerValidator) {
        this.playerState = playerState
        const icon = await uiGameManager.playerAvatarController.getAvatarTexture(this.playerState)
        this.drawBg()
        this.playerIcon.texture = icon
        if(this.playerName != undefined) this.drawPlayerName()
    }

    private removeBgIcon() {
        if(this.bgIcon == undefined) return
        if(this.bgIcon.parent == undefined) return
        this.bgIcon.parent.removeChild(this.bgIcon)
    }

    drawBg() {
        this.removeBgIcon()
        this.bgIcon = new SpriteView(0, 0, this.iconSize, this.iconSize, this, UIGameSpriteHandler.getPlayerBg(this.playerState.color))
        this.removeBgIcon()
        this.addChildAt(this.bgIcon, 0)
    }

    setClickAction(action: (playerColor: PlayerColor) => void) {
        this.interactive = true
        this.cursor = 'pointer'
        this.on('click', () => {
            action(this.playerState.color)
        })
        this.on('tap', () => {
            action(this.playerState.color)
        })
    }

    centerYInParent(addMargin: boolean = false) {
        if(this.parent == undefined) return
        this.y = this.parent.height / 2 - this.height / 2
        if(addMargin) this.y += uiGameManager.canvasController.margin
    }

    get status(): UIGamePlayerAvatarStatus {
        return this._status
    }

    set status(status: UIGamePlayerAvatarStatus) {
        if(this._status == status) return

        this._status = status

        this.removeStatus()

        if(status == UIGamePlayerAvatarStatus.None) return
        if(status == UIGamePlayerAvatarStatus.Embargoed) {
            this.createEmbargoOverlay()
            return
        }

        const statusTexture = this.getStatusTexture()
        if(statusTexture == undefined) return

        const muteIconSizeRatio = 0.53
        const iconSize = this.bgIcon.width * muteIconSizeRatio
        const iconPosition = this.bgIcon.width / 2 + (this.bgIcon.width / 2 - iconSize) / 2

        this.statusIcon = new SpriteView(iconPosition, iconPosition, iconSize, iconSize, this, statusTexture)
    }

    showCreatingCounterOfferWaitIcon() {
        this.waitIcon ??= new UIGameTradeOfferWaitIconView(this, this.playerState.username, 0.45)
        this.waitIcon.show()
    }

    hideCreatingCounterOfferWaitIcon() {
        this.waitIcon?.hide()
    }

    restartCreatingCounterOfferWaitIcon() {
        this.waitIcon?.restart()
    }

    private removeStatus() {
        this.removeStatusIcon()
        this.removeEmbargoOverlay()
    }

    private removeStatusIcon() {
        if(!this.statusIcon) return
        this.removeChild(this.statusIcon)
        this.statusIcon = undefined
    }

    setToolTip(bodyText: TranslationObject[]) {
        const toolTipText: ToolTipText = () => ({title: {key: 'strings:colonistCommon.utils.emptyString'}, body: bodyText})
        uiGameManager.toolTipController.createTip(toolTipText, this)
    }

    unmuteIfPlayer(players: PlayerColor[]): boolean {
        for(const playerColors of players) {
            if(this.playerState.color == playerColors) {
                this.status = UIGamePlayerAvatarStatus.Accepted
                return true
            }
        }
        return false
    }

    private createEmbargoOverlay() {
        this.avatarEmbargoOverlay = new PIXI.Sprite(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.avatar_embargo_overlay))
        this.avatarEmbargoOverlay.anchor.set(0, 0)
        this.avatarEmbargoOverlay.width = this.avatarEmbargoOverlay.height = this.bgIcon.width
        this.addChild(this.avatarEmbargoOverlay)
        this.playerIcon.alpha = 0.5
    }

    private removeEmbargoOverlay() {
        if(this.avatarEmbargoOverlay) this.avatarEmbargoOverlay.destroy()

        this.avatarEmbargoOverlay = undefined
        this.playerIcon.alpha = 1
    }

    private getStatusTexture(): PIXI.Texture | undefined {
        switch(this._status) {
            case UIGamePlayerAvatarStatus.None: return undefined
            case UIGamePlayerAvatarStatus.Accepted: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.player_status_accept)
            case UIGamePlayerAvatarStatus.Rejected: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.player_status_reject)
            case UIGamePlayerAvatarStatus.Pending: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.player_status_pending)
            case UIGamePlayerAvatarStatus.Embargoed: return undefined
        }
    }
}
